import React, { useState } from 'react';
import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import Button from '../components/Button';
import { colors } from '../theme/tokens';
import logoHorizontal from '../assets/logo-horizontal.png';
import { useAuth } from '../contexts/AuthContext';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${colors.neutral.offWhite};
`;

const LoginForm = styled.div`
  background: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 2rem;
`;

const TitleSection = styled.div`
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  text-align: left;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.neutral.darkCharcoal};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-size: 1rem;
  background-color: ${colors.neutral.white};
  color: ${colors.neutral.black};
  
  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
    background-color: ${colors.neutral.offWhite};
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.reds.bright};
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const ForgotPasswordLink = styled.div`
  text-align: center;
  margin-top: 1rem;
  
  a {
    color: ${colors.brand.laurel};
    text-decoration: none;
    font-size: 0.875rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  color: ${colors.neutral.lightCharcoal};
  font-size: 0.875rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${colors.neutral.lightCharcoal};
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: ${colors.brand.laurel};
  border: 1px solid ${colors.brand.laurel};
  width: 100%;
  
  &:hover {
    background-color: ${colors.brand.softLaurel};
  }
  
  &:disabled {
    background-color: transparent;
    border-color: ${colors.neutral.lightCharcoal};
    color: ${colors.neutral.lightCharcoal};
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  background-color: ${colors.brand.laurel};
  color: ${colors.neutral.white};
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: block;
  
  &:hover {
    background-color: ${colors.brand.darkLaurel};
  }
  
  &:disabled {
    background-color: ${colors.neutral.lightCharcoal};
    cursor: not-allowed;
  }
`;

const Login: React.FC = () => {
  const { signIn, signInWithMagicLink } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await signIn(email, password);
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLinkLogin = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setError(null);
    setIsMagicLinkLoading(true);

    try {
      await signInWithMagicLink(email);
      setError('Check your email for the magic link');
    } catch (err: any) {
      setError(err.message || 'An error occurred while sending the magic link');
    } finally {
      setIsMagicLinkLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginForm>
        <Logo src={logoHorizontal} alt="Appetite Admin" />
        <TitleSection>
          <TitleLarge>Hi there 👋</TitleLarge>
          <SubtitleMedium>Sign in to access your dashboard</SubtitleMedium>
        </TitleSection>
        <form onSubmit={handleLogin}>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </PrimaryButton>
          <ForgotPasswordLink>
            <a href="https://creator.appetiteapp.io/forgot-password" target="_blank" rel="noopener noreferrer">Forgot password? Click to reset it</a>
          </ForgotPasswordLink>
          <Divider>or</Divider>
          <Label>Enter your email above to receive a magic link</Label>
          <SecondaryButton 
            type="button" 
            onClick={handleMagicLinkLogin} 
            disabled={isMagicLinkLoading}
          >
            {isMagicLinkLoading ? 'Sending...' : 'Sign in with magic link'}
          </SecondaryButton>
        </form>
      </LoginForm>
    </LoginContainer>
  );
};

export default Login;